/**
 * usePolling — the React shell that drives the {@link nextPollingState} reducer on
 * a relaxed interval. It runs an immediate poll on mount, then re-polls every
 * `intervalMs`, threading the last ETag through each conditional fetch so the
 * daemon can answer `304`. Story C uses it for `GET /runs`; Story D reuses it for
 * `GET /runs/<id>` by passing a different {@link TaggedFetcher}.
 *
 * State is owned by `useReducer` over the pure reducer so the only React-specific
 * concern here is the timer lifecycle and dropping results from a stale fetch
 * after unmount. The pure reducer is unit-tested directly; this hook is exercised
 * end-to-end through the App component test.
 */

import { useEffect, useReducer, useRef } from 'react'
import { ReadApiError } from './client'
import {
  DEFAULT_POLL_INTERVAL_MS,
  INITIAL_POLLING_STATE,
  type PollEvent,
  type PollingState,
  type TaggedFetcher,
  nextPollingState,
} from './polling'

interface UsePollingOptions {
  /** Poll cadence in ms; defaults to {@link DEFAULT_POLL_INTERVAL_MS}. */
  intervalMs?: number
  /** Clock injection for deterministic tests; defaults to `Date.now`. */
  now?: () => number
  /**
   * Endpoint label used only when the fetcher throws a non-{@link ReadApiError}
   * (the fetcher already names the endpoint on its own errors). Story D reuses
   * this hook against `GET /runs/<id>` and passes that path here so a raw throw
   * is reported against the right endpoint instead of a hardcoded `/runs`.
   * Defaults to `/runs` for the Run Overview.
   */
  fallbackEndpoint?: string
  /**
   * A monotonically increasing nonce the caller bumps to force an immediate
   * out-of-band poll (on top of the relaxed interval). It threads the held ETag
   * as usual, so an unchanged snapshot still short-circuits to `304`. The Run
   * Board uses this to re-fetch right after a run cancel so the surface reflects
   * the mutation without waiting for the next tick. Unset means interval-only.
   */
  refreshToken?: number
}

/**
 * Poll `fetcher` on a relaxed interval, retaining the last good payload across
 * `304`s and failures. Returns the live {@link PollingState} for the UI to render.
 */
export function usePolling<T>(fetcher: TaggedFetcher<T>, options: UsePollingOptions = {}): PollingState<T> {
  const intervalMs = options.intervalMs ?? DEFAULT_POLL_INTERVAL_MS
  const now = options.now ?? Date.now
  const fallbackEndpoint = options.fallbackEndpoint ?? '/runs'
  const refreshToken = options.refreshToken ?? 0
  const [state, dispatch] = useReducer(
    nextPollingState as (prev: PollingState<T>, event: PollEvent<T>) => PollingState<T>,
    INITIAL_POLLING_STATE as PollingState<T>
  )

  // The latest ETag is held in a ref, not state, so re-polling never re-renders
  // just to thread the conditional header and the interval closure always reads
  // the freshest tag without being re-created.
  const etagRef = useRef<string | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshToken is a re-arm nonce — bumping it re-runs the effect to fire an immediate poll; it is intentionally not read in the body.
  useEffect(() => {
    let live = true

    const poll = async (): Promise<void> => {
      try {
        const result = await fetcher(etagRef.current)
        if (!live) {
          return
        }
        etagRef.current = result.etag
        dispatch(
          result.status === 'ok' ? { type: 'ok', data: result.data, at: now() } : { type: 'not-modified', at: now() }
        )
      } catch (err) {
        if (!live) {
          return
        }
        const error =
          err instanceof ReadApiError
            ? err
            : new ReadApiError(fallbackEndpoint, 0, err instanceof Error ? err.message : 'error')
        dispatch({ type: 'error', error, at: now() })
      }
    }

    void poll()
    const handle = setInterval(poll, intervalMs)
    return () => {
      live = false
      clearInterval(handle)
    }
  }, [fetcher, intervalMs, now, fallbackEndpoint, refreshToken])

  return state
}
